import { b2Vec2 } from "./box2d-compat.js";
import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const LEVEL13_DYNAMIC_ATLAS = Object.freeze({
  atlasPath: "port/generated/levels_fg/level_13/dynamic.atlas.svg",
  kingStar1: {
    sourceX: 4.5,
    sourceY: 4.5,
    sourceWidth: 151.0,
    sourceHeight: 151.0,
    offsetX: -75.5,
    offsetY: -75.5,
    drawWidth: 151.0,
    drawHeight: 151.0,
  },
  movePlat: {
    sourceX: 345.35,
    sourceY: 42.0,
    sourceWidth: 257.85,
    sourceHeight: 142.4,
    offsetX: -154.65,
    offsetY: -78.0,
    drawWidth: 257.85,
    drawHeight: 142.4,
  },
  greenBarier: {
    sourceX: 692.35,
    sourceY: 24.0,
    sourceWidth: 424.25,
    sourceHeight: 301.0,
    offsetX: -257.65,
    offsetY: -156.0,
    drawWidth: 424.25,
    drawHeight: 301.0,
  },
  killStar: {
    sourceX: 244.5,
    sourceY: 64.5,
    sourceWidth: 31.0,
    sourceHeight: 31.0,
    offsetX: -15.5,
    offsetY: -15.5,
    drawWidth: 31.0,
    drawHeight: 31.0,
  },
});

function drawAtlasSprite(ctx, atlasImage, sprite, x, y, rotationDeg = 0) {
  if (!atlasImage?.complete || !sprite) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(
    atlasImage,
    sprite.sourceX,
    sprite.sourceY,
    sprite.sourceWidth,
    sprite.sourceHeight,
    sprite.offsetX,
    sprite.offsetY,
    sprite.drawWidth,
    sprite.drawHeight,
  );
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  if (rotationDeg) {
    ctx.rotate((rotationDeg * Math.PI) / 180);
  }
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

export class LevelThirteenRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_13/1.visual.runtime.svg";
      this.level13DynamicAtlasImage = new Image();
      this.level13DynamicAtlasImage.src = LEVEL13_DYNAMIC_ATLAS.atlasPath;
    } else {
      this.level13DynamicAtlasImage = null;
    }

    this.createAllLevelBodies();
    this.movePlat1Body = this.getBody("movePlat1");
    this.movePlat2Body = this.getBody("movePlat2");
    this.greenBarierBody = this.getBody("greenBarier");
    this.kingStarBody1 = this.getBody("kingStar1");
    this.killStar1Body = this.getBody("killStar1");
    this.killStar2Body = this.getBody("killStar2");
    if (FlashLevelBaseRuntime.levelFlags[13].greenCheckLevel) {
      this.destroyBody(this.greenBarierBody);
      this.greenBarierBody = null;
    }
    this.addPrismaticJoint(this.movePlat1Body, this.ground, this.movePlat1Body.GetWorldCenter(), new b2Vec2(1, 0));
    this.addPrismaticJoint(this.movePlat2Body, this.ground, this.movePlat2Body.GetWorldCenter(), new b2Vec2(1, 0));
    this.addDistanceJoint(this.killStar1Body, this.ground, this.killStar1Body.GetPosition(), new b2Vec2(this.killStar1Body.GetPosition().x - 100 / 30, this.killStar1Body.GetPosition().y - 100 / 30));
    this.addDistanceJoint(this.killStar2Body, this.ground, this.killStar2Body.GetPosition(), new b2Vec2(this.killStar2Body.GetPosition().x + 100 / 30, this.killStar2Body.GetPosition().y - 100 / 30));
    this.killStar1Body.SetAngularVelocity(10);
    this.killStar2Body.SetAngularVelocity(10);
    this.movePlat1BodyDirection = -1;
    this.movePlat2BodyDirection = -1;
  }

  levelUpdate() {
    if (this.player.y > 500 || [this.kingStarBody1, this.killStar1Body, this.killStar2Body].some((body) => this.contactListener.playerContactBodies.indexOf(body) !== -1)) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
    const movePlat1 = this.getDisplayLayout("movePlat1");
    if (movePlat1.x < -1237) {
      this.movePlat1BodyDirection = 1;
    }
    if (movePlat1.x > -863) {
      this.movePlat1BodyDirection = -1;
    }
    this.movePlat1Body.SetLinearVelocity(new b2Vec2(3 * this.movePlat1BodyDirection, 0));
    const movePlat2 = this.getDisplayLayout("movePlat2");
    if (movePlat2.x < -572) {
      this.movePlat2BodyDirection = 1;
    }
    if (movePlat2.x > -68) {
      this.movePlat2BodyDirection = -1;
    }
    this.movePlat2Body.SetLinearVelocity(new b2Vec2(3 * this.movePlat2BodyDirection, 0));
    if (this.hitNamedObject("greenCheck") && this.greenBarierBody) {
      FlashLevelBaseRuntime.levelFlags[13].greenCheckLevel = true;
      this.destroyBody(this.greenBarierBody);
      this.greenBarierBody = null;
    }
  }

  drawLevel13Bodies(ctx) {
    const kingStarLayout = this.getDisplayLayout("kingStar1");
    if (kingStarLayout) {
      drawAtlasSprite(
        ctx,
        this.level13DynamicAtlasImage,
        LEVEL13_DYNAMIC_ATLAS.kingStar1,
        kingStarLayout.x,
        kingStarLayout.y,
        kingStarLayout.rotation ?? 0,
      );
    }

    for (const name of ["movePlat1", "movePlat2"]) {
      const layout = this.getDisplayLayout(name);
      if (!layout) {
        continue;
      }
      drawAtlasSprite(
        ctx,
        this.level13DynamicAtlasImage,
        LEVEL13_DYNAMIC_ATLAS.movePlat,
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }

    if (this.greenBarierBody) {
      const greenBarierLayout = this.getDisplayLayout("greenBarier");
      if (greenBarierLayout) {
        drawAtlasSprite(
          ctx,
          this.level13DynamicAtlasImage,
          LEVEL13_DYNAMIC_ATLAS.greenBarier,
          greenBarierLayout.x,
          greenBarierLayout.y,
          greenBarierLayout.rotation ?? 0,
        );
      }
    }

    for (const name of ["killStar1", "killStar2"]) {
      const layout = this.getDisplayLayout(name);
      if (!layout) {
        continue;
      }
      drawAtlasSprite(
        ctx,
        this.level13DynamicAtlasImage,
        LEVEL13_DYNAMIC_ATLAS.killStar,
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart && this.backgroundSymbol?.layout) {
      const sym = this.backgroundSymbol;
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      ctx.save();
      ctx.translate(bx, by);
      if (sym.pngLayout) {
        const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
        const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
        ctx.drawImage(
          this.backgroundImage,
          sym.pngLayout.cropX, sym.pngLayout.cropY,
          sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
          sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
          sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
          sym.pngLayout.cropWidth * scaleX,
          sym.pngLayout.cropHeight * scaleY,
        );
      } else {
        ctx.drawImage(this.backgroundImage, sym.layout.imageOffsetX, sym.layout.imageOffsetY);
      }
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    this.drawLevel13Bodies(ctx);

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, this.goalPosition.x, this.goalPosition.y);

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
